<template>
  <div>
    <h3>Custom Event Questions</h3>
    <p><small>This is a list of custom questions your business has available for adding to any of its events.</small></p>
    
    <div class="border-top pt-2 mb-3">
      <b-row class="mb-2">
        <b-col cols="12" md="4">
          <label for="customQuestionLabel">Custom Question</label>
        </b-col>
        <b-col cols="12" md="7">
          <label for="customQuestionResponseOptions">Response Options</label>
        </b-col>
        <b-col cols="12" md="1"></b-col>
      </b-row>
      <b-row v-for="(question, i) in eventQuestions" class="mb-2 border-bottom" :key="i">
        <b-col cols="12" md="4">
          {{ question.label }}
        </b-col>
        <b-col cols="12" md="7">
          <b-form-select
            v-if="question.options.length"
            :options="question.options"
            size="sm"
            text-field="label"
            value-field="@id"
          >
            <template #first>
              <option value="null" selected="selected" disabled>Available Options</option>
            </template>
          </b-form-select>
          <p v-else class="text-warning">Manual Response</p>
        </b-col>
        <b-col cols="12" md="1">
          <b-button-group size="sm">
            <b-button
              variant="link"
              @click="loadEventQuestionById(question.id)"
            >
              Edit
            </b-button>
            <b-button
              variant="link"
              @click="deleteQuestion(question.id)"
            >
              delete
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>
    </div>
    <b-form @submit="onSubmit" @reset="onReset">
      <b-form-group>
        <label for="customQuestion">Custom Question</label>
        <b-form-input
          id="customQuestionLabel"
          v-model="customQuestionLabel"
          type="text"
          size="sm"
          placeholder="Enter your custom question here"
          required
        ></b-form-input>
      </b-form-group>
      <template v-if="customQuestionId">
        <b-form-group v-for="(option, index) in customQuestionResponseOptions" :key="index">
          <b-input-group size="sm">
            <b-form-input
              :id="'customQuestionResponseOption' + index"
              v-model="option.label"
              type="text"
              placeholder="Enter response option"
              required
            ></b-form-input>
            <b-input-group-append>
              <b-button
                variant="danger"
                @click="customQuestionResponseOptions.splice(index, 1)"
              >
                Remove Option
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
        
      </template>
      <b-form-group>
        <label for="customQuestionResponseOptions">Response Options</label>
        <b-form-textarea
          id="customQuestionResponseOptions"
          v-model="customQuestionResponseOptionsText"
          type="text"
          size="sm"
          placeholder="Enter new options separated by new lines (i.e., press Enter after each option), or LEAVE BLANK FOR MANUAL RESPONSE"
          rows="8"
          @input="setCustomQuestionResponseOptions"
        ></b-form-textarea>
      </b-form-group>
      <b-button type="submit" variant="primary" size="sm">Submit</b-button>
      <b-button type="reset" variant="secondary" size="sm">Reset</b-button>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'EventCustomQuestions',
  data: () => {
    return {
      customQuestionId: null,
      customQuestionIri: null,
      // This will hold the label of the custom question
      customQuestionLabel: '',
      // This will hold the response options as a text area input
      // where each option is separated by a new line
      customQuestionResponseOptionsText: '',
      // This will hold the response options in a format suitable for the API
      customQuestionResponseOptions: [],
      customQuestionResponseOptionsNew: []
    }
  },
  computed: {
    ...mapGetters([
      'eventQuestions'
    ])
  },

  methods: {
    ...mapActions([
      'getEventQuestions',
      'postEventQuestion',
      'putEventQuestion',
      'deleteEventQuestion',
      'postEventQuestionResponse',
      'putEventQuestionResponse',
      'deleteEventQuestionResponse'
    ]),
    loadEventQuestionById (id) {
      let question = this.eventQuestions.find(question => question.id === id)
      if (question) {
        this.customQuestionId = question.id
        this.customQuestionIri = question['@id']
        this.customQuestionLabel = question.label
        this.customQuestionResponseOptions = question.options.length ? [...question.options] : []
        // this.customQuestionResponseOptionsText = question.options.map(option => option.label).join('\n')
      } else {
        this.customQuestionId = null
        this.customQuestionIri = null
        this.customQuestionLabel = ''
        this.customQuestionResponseOptions = []
        this.customQuestionResponseOptionsNew = []
        this.customQuestionResponseOptionsText = ''
      }
    },
    deleteQuestion (id) {
      const msg = 'Are you sure you want to delete this question? THIS CANNOT BE UNDONE!'
      this.$bvModal.msgBoxConfirm(msg, {
        title: 'Please Confirm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      .then(async confirmed => {
        if (confirmed) {
          let promises = []
          // delete the question
          // remove options
          let existingOptions = this.eventQuestions.find(q => q.id === id).options
          existingOptions.forEach((existingOption) => {
            // This option was removed, delete it
            promises.push(this.deleteEventQuestionResponse(existingOption.id))
          })
          await Promise.all(promises);
          await this.deleteEventQuestion(id)
          // Reset the form after deletion
          this.onReset()
        }
      })
    },
    setCustomQuestionResponseOptions () {
      // Split the text area input by new lines and trim each option
      let customQuestionResponseOptions = this.customQuestionResponseOptionsText
        .split('\n')
        .map(option => ({ label: option.trim() }))
        .filter(option => option.label) // Remove empty options
      // If the customQuestionId is set, we need to ensure the options are in the correct format
      if (this.customQuestionId) {
        customQuestionResponseOptions = customQuestionResponseOptions.map(option => {
          return {
            label: option.label,
            id: null // Set id to null for new options
          }
        })
      }
      // If the customQuestionId is not set, we can keep the options as they are
      else {
        customQuestionResponseOptions = customQuestionResponseOptions.map(option => {
          return {
            label: option.label
          }
        })
      }
      for (let option of customQuestionResponseOptions) {
        // only allow alphanumeric characters, spaces, and basic punctuation
        if (!/^[a-zA-Z0-9\s.,!?;:'"-]+$/.test(option.label)) {
          alert('Response options can only contain alphanumeric characters, spaces, and basic punctuation.')
          return
        }
      }
      
      this.customQuestionResponseOptionsNew = customQuestionResponseOptions
    },
    validateOptinos () {
      // if no options are provided, return true
      if (this.customQuestionResponseOptions.length === 0) {
        return true
      }
      for (let option of this.customQuestionResponseOptions) {
        // Check for empty labels
        if (!option.label.trim()) {
          alert('Response options cannot be empty.')
          return false
        }
      }
      for (let option of this.customQuestionResponseOptions) {
        // Check for duplicate labels
        if (this.customQuestionResponseOptions.filter(o => o.label.trim() === option.label.trim()).length > 1) {
          alert('Response options must be unique.')
          return false
        }
      }
      for (let option of this.customQuestionResponseOptions) {
        // only allow alphanumeric characters, spaces, and basic punctuation
        if (!/^[a-zA-Z0-9\s.,!?;:'"-]+$/.test(option.label)) {
          alert('Response options can only contain alphanumeric characters, spaces, and basic punctuation.')
          return false
        }
      }
      // make sure more than one option is provided
      if (this.customQuestionResponseOptions.length < 2) {
        alert('Please provide at least two response options.')
        return false
      }
      // If all checks pass, return true
      return true
    },
    async onSubmit (event) {
      event.preventDefault()
      // merge the new response options with the existing ones
      this.customQuestionResponseOptionsNew.forEach(option => {
        // Check if the option already exists in the current question's options
        let existingOption = this.customQuestionResponseOptions.find(o => o.label === option.label)
        if (!existingOption) {
          // If it doesn't exist, add it to the response options
          this.customQuestionResponseOptions.push(option)
        }
      })
      // Validate the response options
      if (!this.validateOptinos()) {
        return
      }
      if (!this.customQuestionId) {
        let promises = []
        // Create new question
        if (!this.customQuestionLabel.trim()) {
          alert('Please enter a question label.')
          return
        }
        // console.log('Creating new question with label:', this.customQuestionLabel)
        this.customQuestionResponseOptions = this.customQuestionResponseOptions.filter(option => option.label.trim() !== '')
        // console.log('Response Options:', this.customQuestionResponseOptions)
        // return
        // post the question
        let question = await this.postEventQuestion({
          business: this.currentBusiness['@id'],
          label: this.customQuestionLabel
        })
        // Post each response option
        this.customQuestionResponseOptions.forEach(async option => {
          promises.push(this.postEventQuestionResponse({
            question: question['@id'],
            label: option.label
          }))
        })
        // Wait for all response options to be created
        await Promise.all(promises)
        // Reset the form after submission
        this.onReset()
      } else {
        let promises = []
        // Update existing question
        await this.putEventQuestion({
          id: this.customQuestionId,
          label: this.customQuestionLabel
        })
        // Update the response options
        this.customQuestionResponseOptions.forEach(async (option) => {
          // Check if the option already exists in the current question's options
          let existingOption = this.eventQuestions.find(q => q.id === this.customQuestionId).options.find(o => o.label === option.label)
          if (existingOption) {
            option.id = existingOption.id // Set the id to the existing option's id
          } else {
            option.id = null // New option, no id yet
          }

          if (option.id) {
            // Update existing response option
            promises.push(this.putEventQuestionResponse({
              id: option.id,
              label: option.label
            }))
          } else {
            // Create new response option
            promises.push(this.postEventQuestionResponse({
              question: this.customQuestionIri,
              label: option.label
            }))
          }
        })
        // look for removed options
        let existingOptions = this.eventQuestions.find(q => q.id === this.customQuestionId).options
        existingOptions.forEach(async (existingOption) => {
          if (this.customQuestionResponseOptions.find(o => o.label === existingOption.label) === undefined) {
            // This option was removed, delete it
            promises.push(this.deleteEventQuestionResponse(existingOption.id))
          }
        })
        await Promise.all(promises);
        // Reset the form after submission
        this.onReset()
      }
    },
    async onReset () {
      await this.getEventQuestions({
        'business.id': this.currentBusiness.id,
      })
      this.customQuestionId = null
      this.customQuestionIri = null
      this.customQuestionLabel = ''
      this.customQuestionResponseOptionsText = ''
      // Reset the response options to null
      this.customQuestionResponseOptions = []
      this.customQuestionResponseOptionsNew = []
    }
  },

  created () {
    this.getEventQuestions({
      'business.id': this.currentBusiness.id,
    })
  },
}
</script>
